import React, { useEffect, useMemo, useRef, useState } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import interactionPlugin from '@fullcalendar/interaction';
import itLocale from '@fullcalendar/core/locales/it';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  IconButton,
  Popover,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import AccessTimeOutlinedIcon from '@mui/icons-material/AccessTimeOutlined';
import CloseIcon from '@mui/icons-material/Close';
import UnfoldMoreOutlinedIcon from '@mui/icons-material/UnfoldMoreOutlined';
import {
  EventClickArg,
  EventContentArg,
  EventInput,
} from '@fullcalendar/core';
import { DateClickArg } from '@fullcalendar/interaction';
import { pollApprovedBookings } from '../services/bookings';
import { isAdminAuthenticated } from '../services/adminAuth';
import { Booking } from '../types/booking';
import { colors } from '../theme';

const CARD_RADIUS = 28;
const CONTROL_RADIUS = 20;
const OCCUPIED_LABEL = 'Occupato';

function formatEventDate(date: string): string {
  const [, month, day] = date.split('-');
  return `${day}/${month}`;
}

function TimeChip({
  booking,
  compact,
}: {
  booking: Pick<Booking, 'startDate' | 'endDate' | 'startTime' | 'endTime'>;
  compact?: boolean;
}) {
  const sameDay = booking.startDate === booking.endDate;
  const iconSize = compact ? 10 : 14;
  const badgeSize = compact ? 14 : 20;

  return (
    <Box
      sx={{
        alignSelf: 'flex-start',
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 0.45 : 0.75,
        maxWidth: '100%',
        minWidth: 0,
        pl: compact ? 0.3 : 0.45,
        pr: compact ? 0.55 : 1,
        py: compact ? 0.15 : 0.45,
        borderRadius: '999px',
        background: `linear-gradient(135deg, ${colors.primaryContainer} 0%, ${colors.secondaryContainer} 100%)`,
        color: colors.onPrimaryContainer,
        border: `1px solid ${colors.primary}22`,
        boxShadow: `inset 0 1px 0 ${colors.onPrimary}66, 0 1px 3px ${colors.primary}28`,
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          width: badgeSize,
          height: badgeSize,
          borderRadius: '50%',
          bgcolor: colors.primary,
          color: colors.onPrimary,
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AccessTimeOutlinedIcon sx={{ fontSize: iconSize }} />
      </Box>

      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.4,
          minWidth: 0,
          overflow: 'hidden',
          fontWeight: 700,
          fontSize: compact ? '0.58rem' : '0.8rem',
          lineHeight: 1.25,
          letterSpacing: '0.01em',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        <Box component="span" sx={{ whiteSpace: 'nowrap' }}>
          {formatEventDate(booking.startDate)}
          <Box component="span" sx={{ mx: 0.3, opacity: 0.4, fontWeight: 500 }}>
            ·
          </Box>
          {booking.startTime}
        </Box>
        <Box
          component="span"
          sx={{ flexShrink: 0, opacity: 0.35, fontWeight: 600 }}
        >
          {sameDay ? '–' : '→'}
        </Box>
        <Box
          component="span"
          sx={{
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {sameDay ? (
            booking.endTime
          ) : (
            <>
              {formatEventDate(booking.endDate)}
              <Box
                component="span"
                sx={{ mx: 0.3, opacity: 0.4, fontWeight: 500 }}
              >
                ·
              </Box>
              {booking.endTime}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}

function EventLabel(arg: EventContentArg) {
  const booking = arg.event.extendedProps.booking as
    | Pick<Booking, 'startDate' | 'endDate' | 'startTime' | 'endTime'>
    | undefined;
  const isTimeGrid = arg.view.type.startsWith('timeGrid');

  if (!booking) {
    return (
      <Box component="span" sx={{ fontWeight: 600, px: 0.25 }}>
        {arg.event.title}
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 0.35,
        width: '100%',
        minWidth: 0,
        px: 0.1,
        py: 0.1,
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.35,
          minWidth: 0,
        }}
      >
        <Box
          component="span"
          sx={{
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            fontWeight: 700,
            fontSize: isTimeGrid ? '0.82rem' : '0.74rem',
            lineHeight: 1.25,
            letterSpacing: '-0.01em',
            px: 0.15,
          }}
        >
          {arg.event.title}
        </Box>
        <UnfoldMoreOutlinedIcon
          sx={{
            flexShrink: 0,
            fontSize: 13,
            opacity: 0.7,
          }}
        />
      </Box>
      <TimeChip booking={booking} compact />
    </Box>
  );
}

interface BookingsCalendarProps {
  /** Altezza contenuto calendario (opzionale) */
  compact?: boolean;
  /** Se true mostra i nomi dei gruppi; altrimenti “Occupato”. */
  isAdmin?: boolean;
}

export function BookingsCalendar({
  compact = false,
  isAdmin: isAdminProp,
}: BookingsCalendarProps) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const calendarRef = useRef<FullCalendar>(null);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isAdmin, setIsAdmin] = useState(Boolean(isAdminProp));
  const [detail, setDetail] = useState<{
    booking: Booking;
    anchorEl: HTMLElement;
  } | null>(null);

  useEffect(() => {
    if (isAdminProp) {
      setIsAdmin(true);
      return;
    }
    let cancelled = false;
    isAdminAuthenticated().then((ok) => {
      if (!cancelled) setIsAdmin(ok);
    });
    return () => {
      cancelled = true;
    };
  }, [isAdminProp]);

  useEffect(() => {
    return pollApprovedBookings(
      (data) => {
        setBookings(data);
        setLoading(false);
        setError(null);
      },
      (err) => {
        setError(err.message);
        setLoading(false);
      }
    );
  }, []);

  const events: EventInput[] = useMemo(
    () =>
      bookings.map((b) => ({
        id: b.id,
        title: isAdmin ? b.gruppoScout : OCCUPIED_LABEL,
        start: `${b.startDate}T${b.startTime}:00`,
        end: `${b.endDate}T${b.endTime}:00`,
        backgroundColor: colors.primary,
        borderColor: colors.primary,
        textColor: colors.onPrimary,
        extendedProps: {
          booking: {
            startDate: b.startDate,
            endDate: b.endDate,
            startTime: b.startTime,
            endTime: b.endTime,
          },
        },
      })),
    [bookings, isAdmin]
  );

  const handleEventClick = (arg: EventClickArg) => {
    const booking = bookings.find((b) => b.id === arg.event.id);
    if (!booking) return;
    arg.jsEvent.preventDefault();
    setDetail({
      booking,
      anchorEl: arg.el,
    });
  };

  const handleDateClick = (arg: DateClickArg) => {
    if (arg.view.type !== 'dayGridMonth') return;
    calendarRef.current?.getApi().changeView('timeGridDay', arg.date);
  };

  const contentHeight = compact
    ? isMobile
      ? 380
      : 480
    : isMobile
      ? 460
      : 600;

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ borderRadius: 3, mb: 2 }}>
          {error}
        </Alert>
      )}

      <Card
        sx={{
          borderRadius: `${CARD_RADIUS}px`,
          overflow: 'hidden',
          border: `1px solid ${colors.outlineVariant}`,
        }}
      >
        <CardContent
          sx={{
            p: { xs: 1.5, sm: 2.5 },
            '&:last-child': { pb: { xs: 1.5, sm: 2.5 } },
          }}
        >
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Box
              sx={{
                '& .fc': {
                  '--fc-border-color': colors.outlineVariant,
                  '--fc-page-bg-color': 'transparent',
                  '--fc-neutral-bg-color': colors.surfaceContainerLow,
                  '--fc-today-bg-color': `${colors.primaryContainer}55`,
                  '--fc-button-bg-color': colors.primary,
                  '--fc-button-border-color': colors.primary,
                  '--fc-button-hover-bg-color': '#3A2268',
                  '--fc-button-hover-border-color': '#3A2268',
                  '--fc-button-active-bg-color': colors.primaryContainer,
                  '--fc-button-active-border-color': colors.primaryContainer,
                  '--fc-button-active-text-color': colors.onPrimaryContainer,
                  '--fc-event-bg-color': colors.primary,
                  '--fc-event-border-color': colors.primary,
                  fontFamily: 'Roboto, Helvetica, Arial, sans-serif',
                },
                '& .fc .fc-toolbar': {
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: { xs: 1.25, sm: 1.5 },
                  alignItems: 'center',
                  marginBottom: { xs: 1.75, sm: 2.25 },
                  rowGap: 1.5,
                },
                '& .fc .fc-toolbar-chunk': {
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap',
                },
                '& .fc .fc-toolbar-title': {
                  fontSize: { xs: '1.15rem', sm: '1.35rem' },
                  fontWeight: 700,
                  color: colors.primary,
                  letterSpacing: '-0.01em',
                  margin: 0,
                  paddingInline: 0.5,
                },
                '& .fc .fc-button-primary': {
                  backgroundColor: colors.primary,
                  borderColor: 'transparent',
                  color: colors.onPrimary,
                  borderRadius: `${CONTROL_RADIUS}px`,
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  boxShadow: 'none',
                  padding: '0.45em 1em',
                  lineHeight: 1.4,
                  outline: 'none',
                },
                '& .fc .fc-button-primary:not(:disabled):hover': {
                  backgroundColor: '#3A2268',
                  borderColor: 'transparent',
                  color: colors.onPrimary,
                },
                '& .fc .fc-button-primary:not(:disabled).fc-button-active, & .fc .fc-button-primary:not(:disabled):active':
                  {
                    backgroundColor: colors.primaryContainer,
                    borderColor: 'transparent',
                    color: colors.onPrimaryContainer,
                    boxShadow: 'none',
                  },
                '& .fc .fc-button-primary:disabled': { opacity: 0.45 },
                '& .fc .fc-button-primary:focus': {
                  boxShadow: `0 0 0 3px ${colors.primaryContainer}`,
                },
                '& .fc .fc-button-group': {
                  gap: 0,
                  borderRadius: `${CONTROL_RADIUS}px`,
                  overflow: 'hidden',
                  border: `1px solid ${colors.outlineVariant}`,
                  backgroundColor: colors.surfaceContainerLow,
                },
                '& .fc .fc-button-group > .fc-button': {
                  borderRadius: 0,
                  margin: 0,
                  border: 'none',
                  backgroundColor: 'transparent',
                  color: colors.onSurfaceVariant,
                  boxShadow: 'none',
                },
                '& .fc .fc-button-group > .fc-button:hover': {
                  backgroundColor: colors.secondaryContainer,
                  color: colors.onSecondaryContainer,
                },
                '& .fc .fc-button-group > .fc-button.fc-button-active': {
                  backgroundColor: colors.primary,
                  color: colors.onPrimary,
                },
                '& .fc .fc-today-button': {
                  marginLeft: 0,
                  borderRadius: `${CONTROL_RADIUS}px`,
                  border: `1px solid ${colors.outlineVariant}`,
                  backgroundColor: colors.surfaceContainerLow,
                  color: colors.onSurfaceVariant,
                },
                '& .fc .fc-today-button:not(:disabled):hover': {
                  backgroundColor: colors.secondaryContainer,
                  color: colors.onSecondaryContainer,
                  borderColor: 'transparent',
                },
                '& .fc .fc-today-button:disabled': { opacity: 0.4 },
                '& .fc .fc-view-harness': {
                  borderRadius: `${CARD_RADIUS - 8}px`,
                  overflow: 'hidden',
                  border: `1px solid ${colors.outlineVariant}`,
                  backgroundColor: colors.surfaceContainer,
                },
                '& .fc .fc-scrollgrid': {
                  border: 'none !important',
                  borderRadius: `${CARD_RADIUS - 8}px`,
                },
                '& .fc-theme-standard td, & .fc-theme-standard th': {
                  borderColor: colors.outlineVariant,
                },
                '& .fc .fc-col-header-cell': {
                  padding: '10px 4px',
                  backgroundColor: colors.surfaceContainerLow,
                },
                '& .fc .fc-col-header-cell-cushion': {
                  fontWeight: 600,
                  fontSize: '0.8rem',
                  color: colors.onSurfaceVariant,
                  textDecoration: 'none',
                  padding: '4px 6px',
                },
                '& .fc .fc-daygrid-day': {
                  cursor: 'pointer',
                },
                '& .fc .fc-daygrid-day-number': {
                  fontWeight: 600,
                  fontSize: '0.85rem',
                  color: colors.onSurface,
                  padding: '8px 10px 4px',
                  textDecoration: 'none',
                },
                '& .fc .fc-daygrid-day-frame': {
                  minHeight: {
                    xs: compact ? 72 : 84,
                    sm: compact ? 92 : 108,
                  },
                  padding: '2px',
                },
                '& .fc .fc-daygrid-event': {
                  borderRadius: '12px',
                  padding: '4px 6px',
                  marginTop: '2px',
                  border: `1.5px solid ${colors.onPrimary}66`,
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  cursor: 'pointer',
                  minHeight: 36,
                  boxShadow: `0 4px 12px ${colors.primary}77, 0 2px 4px rgba(0,0,0,0.28)`,
                },
                '& .fc .fc-daygrid-block-event .fc-event-main': {
                  padding: 0,
                },
                '& .fc .fc-daygrid-more-link': {
                  fontWeight: 600,
                  color: colors.secondary,
                  marginTop: '2px',
                },
                '& .fc .fc-timegrid-event': {
                  borderRadius: '12px',
                  border: `2px solid ${colors.onPrimary}88`,
                  boxShadow: `0 6px 18px ${colors.primary}88, 0 3px 8px rgba(0,0,0,0.35)`,
                  cursor: 'pointer',
                  padding: '4px 6px',
                },
                '& .fc .fc-timegrid-event.fc-event-mirror, & .fc .fc-timegrid-event:hover':
                  {
                    boxShadow: `0 8px 22px ${colors.primary}99, 0 4px 10px rgba(0,0,0,0.4)`,
                    zIndex: 5,
                  },
                '& .fc .fc-timegrid-event .fc-event-main': {
                  padding: 0,
                },
                '& .fc .fc-timegrid-event-harness': {
                  marginRight: 3,
                },
                '& .fc .fc-timegrid-event-harness > .fc-timegrid-event': {
                  filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.25))',
                },
                '& .fc .fc-event-main': { cursor: 'pointer' },
                '& .fc .fc-event-main-frame': {
                  height: 'auto',
                },
              }}
            >
              <FullCalendar
                ref={calendarRef}
                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                initialView="dayGridMonth"
                headerToolbar={{
                  left: 'prev,next today',
                  center: 'title',
                  right: 'dayGridMonth,timeGridDay',
                }}
                locale={itLocale}
                height="auto"
                contentHeight={contentHeight}
                events={events}
                eventClick={handleEventClick}
                dateClick={handleDateClick}
                eventContent={EventLabel}
                displayEventTime={false}
                displayEventEnd={false}
                editable={false}
                selectable={false}
                nowIndicator
                slotMinTime="08:00:00"
                slotMaxTime="22:00:00"
                allDaySlot={false}
                dayMaxEvents={isMobile ? 2 : 3}
                buttonText={{
                  today: 'Oggi',
                  month: 'Mese',
                  day: 'Giorno',
                }}
              />
            </Box>
          )}
        </CardContent>
      </Card>

      <Popover
        open={Boolean(detail)}
        anchorEl={detail?.anchorEl ?? null}
        onClose={() => setDetail(null)}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.75,
              mx: 1,
              maxWidth: { xs: 'calc(100vw - 24px)', sm: 340 },
              width: { xs: 'min(100%, 320px)', sm: 340 },
              borderRadius: '20px',
              border: `1px solid ${colors.outlineVariant}`,
              boxShadow: `0 10px 28px ${colors.primary}28`,
              overflow: 'hidden',
              bgcolor: colors.surfaceContainer,
            },
          },
        }}
      >
        {detail && (
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'flex-start',
                justifyContent: 'space-between',
                gap: 1,
                mb: 1.5,
              }}
            >
              <Typography
                sx={{
                  fontWeight: 700,
                  color: colors.primary,
                  fontSize: '1.05rem',
                  lineHeight: 1.3,
                  pr: 0.5,
                }}
              >
                {isAdmin ? detail.booking.gruppoScout : OCCUPIED_LABEL}
              </Typography>
              <IconButton
                aria-label="Chiudi"
                size="small"
                onClick={() => setDetail(null)}
                edge="end"
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Box>

            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 0.75,
              }}
            >
              <Box
                sx={{
                  px: 1.1,
                  py: 0.75,
                  borderRadius: '12px',
                  bgcolor: colors.surfaceContainerLow,
                  border: `1px solid ${colors.outlineVariant}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.onSurfaceVariant,
                    fontWeight: 600,
                    fontSize: '0.68rem',
                    lineHeight: 1.2,
                  }}
                >
                  Inizio
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    mt: 0.15,
                    fontSize: '0.84rem',
                    lineHeight: 1.25,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatEventDate(detail.booking.startDate)}
                  <Box
                    component="span"
                    sx={{ mx: 0.4, color: colors.onSurfaceVariant, fontWeight: 500 }}
                  >
                    ·
                  </Box>
                  <Box component="span" sx={{ color: colors.primary }}>
                    {detail.booking.startTime}
                  </Box>
                </Typography>
              </Box>
              <Box
                sx={{
                  px: 1.1,
                  py: 0.75,
                  borderRadius: '12px',
                  bgcolor: colors.surfaceContainerLow,
                  border: `1px solid ${colors.outlineVariant}`,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.onSurfaceVariant,
                    fontWeight: 600,
                    fontSize: '0.68rem',
                    lineHeight: 1.2,
                  }}
                >
                  Fine
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    mt: 0.15,
                    fontSize: '0.84rem',
                    lineHeight: 1.25,
                    fontVariantNumeric: 'tabular-nums',
                  }}
                >
                  {formatEventDate(detail.booking.endDate)}
                  <Box
                    component="span"
                    sx={{ mx: 0.4, color: colors.onSurfaceVariant, fontWeight: 500 }}
                  >
                    ·
                  </Box>
                  <Box component="span" sx={{ color: colors.primary }}>
                    {detail.booking.endTime}
                  </Box>
                </Typography>
              </Box>
            </Box>

            {isAdmin && (
              <Box
                sx={{
                  mt: 1.75,
                  pt: 1.75,
                  borderTop: `1px solid ${colors.outlineVariant}`,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    color: colors.onSurfaceVariant,
                    fontWeight: 600,
                    letterSpacing: '0.04em',
                    textTransform: 'uppercase',
                  }}
                >
                  Dettagli prenotazione
                </Typography>
                <Typography variant="body2">
                  <Box component="strong" sx={{ color: colors.onSurface }}>
                    Referente:
                  </Box>{' '}
                  {detail.booking.referenteNome}
                </Typography>
                <Typography variant="body2">
                  <Box component="strong" sx={{ color: colors.onSurface }}>
                    Telefono:
                  </Box>{' '}
                  <Box
                    component="a"
                    href={`tel:${detail.booking.telefono}`}
                    sx={{ color: colors.primary, textDecoration: 'none' }}
                  >
                    {detail.booking.telefono}
                  </Box>
                </Typography>
                <Typography variant="body2">
                  <Box component="strong" sx={{ color: colors.onSurface }}>
                    Email:
                  </Box>{' '}
                  {detail.booking.email ? (
                    <Box
                      component="a"
                      href={`mailto:${detail.booking.email}`}
                      sx={{ color: colors.primary, textDecoration: 'none' }}
                    >
                      {detail.booking.email}
                    </Box>
                  ) : (
                    '—'
                  )}
                </Typography>
                <Typography variant="body2">
                  <Box component="strong" sx={{ color: colors.onSurface }}>
                    Branca:
                  </Box>{' '}
                  {detail.booking.branca || '—'}
                </Typography>
                <Typography variant="body2">
                  <Box component="strong" sx={{ color: colors.onSurface }}>
                    Ragazzi previsti:
                  </Box>{' '}
                  {detail.booking.numeroRagazzi}
                </Typography>
                <Typography variant="body2">
                  <Box component="strong" sx={{ color: colors.onSurface }}>
                    Adulti previsti:
                  </Box>{' '}
                  {detail.booking.numeroAdulti ?? 0}
                </Typography>
                {detail.booking.altreInfo?.trim() ? (
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                    <Box component="strong" sx={{ color: colors.onSurface }}>
                      Altre informazioni:
                    </Box>{' '}
                    {detail.booking.altreInfo.trim()}
                  </Typography>
                ) : null}
              </Box>
            )}
          </Box>
        )}
      </Popover>
    </Box>
  );
}
